import { useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { Formik } from 'formik';

import { CancelButton, PrimaryButton } from 'src/components/ui/Buttons';

import { ManagementModalProps } from './types';
import { darkGreen, primaryColor } from 'src/data/colors';

const useStyles = makeStyles(() => ({
  container: {
    padding: '20px',
  },
  colorProgress: {
    color: primaryColor,
  },
  button: {
    background: primaryColor,
    '&:hover': {
      background: darkGreen,
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: primaryColor,
      },
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: darkGreen,
    },
  },
}));

const ManagementModal: React.FC<ManagementModalProps> = ({
  fields,
  initialValues,
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
  title,
  validationSchema,
}) => {
  const classes = useStyles();
  const [blurredFields, setBlurredFields] = useState<string[]>([]);

  const handleOnBlur = (id: string) => {
    setBlurredFields((prevFields) => [...prevFields, id]);
  };

  const handleFocus = (id: string) => {
    setBlurredFields((prevFields) => prevFields.filter((field) => field !== id));
  };

  return (
    <Dialog open={isOpen} maxWidth="sm" fullWidth>
      <DialogContent className={classes.container}>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          children={({
            dirty,
            errors,
            handleBlur,
            handleChange,
            isValid,
            isValidating,
            touched,
            values,
          }) => {
            return (
              <form>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Typography variant="h5">{title}</Typography>
                  </Grid>
                  {fields?.length &&
                    fields.map(({ id, label, disabled, list }) => (
                      <Grid item key={id}>
                        {list?.length ? (
                          <TextField
                            fullWidth
                            id={id}
                            label={label}
                            name={id}
                            onChange={handleChange}
                            onFocus={() => handleFocus(id)}
                            size="small"
                            variant="outlined"
                            onBlur={(e) => {
                              handleBlur(e);
                              handleOnBlur(id);
                            }}
                            className={classes.textField}
                            disabled={isSubmitting || disabled}
                            error={touched[id] && Boolean(errors[id])}
                            value={values[id] || ''}
                            select
                            SelectProps={{
                              native: true,
                            }}
                            helperText={
                              blurredFields.includes(id) && touched[id] && errors[id]
                                ? errors[id]
                                : null
                            }
                          >
                            <option value="" key="" />
                            {list.map((option) => (
                              <option key={option.id} value={option.id as number}>
                                {option.name}
                              </option>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            fullWidth
                            id={id}
                            label={label}
                            name={id}
                            onChange={handleChange}
                            onFocus={() => handleFocus(id)}
                            size="small"
                            variant="outlined"
                            onBlur={(e) => {
                              handleBlur(e);
                              handleOnBlur(id);
                            }}
                            className={classes.textField}
                            disabled={isSubmitting || disabled}
                            error={touched[id] && Boolean(errors[id])}
                            value={values[id] || ''}
                            helperText={
                              blurredFields.includes(id) && touched[id] && errors[id]
                                ? errors[id]
                                : null
                            }
                          />
                        )}
                      </Grid>
                    ))}
                  <Grid item container justifyContent="flex-end" spacing={1}>
                    <Grid item style={{ marginRight: 'auto' }}>
                      <CancelButton disabled={isSubmitting} onClick={onClose}>
                        Cancel
                      </CancelButton>
                    </Grid>
                    <Grid item>
                      <PrimaryButton
                        className={classes.button}
                        disabled={isSubmitting || isValidating || !isValid || !dirty}
                        onClick={() => onSubmit(values)}
                        type="button"
                      >
                        {isSubmitting ? (
                          <CircularProgress
                            classes={{ colorPrimary: classes.colorProgress }}
                            size={24}
                          />
                        ) : (
                          'Save'
                        )}
                      </PrimaryButton>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            );
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ManagementModal;
